import type { LocalStreamStats, VideoEncoderConfig } from '@volcengine/rtc';
import { type LiveDebugLogger, type LiveDebugValue, sanitizeString } from '../live-socket';
import { hasUsableCameraVideoTrack, isRtcCameraGumStream, shouldReacquireCameraOnForeground } from './camera-lifecycle';
import {
  ensureVolcRtcEngine,
  getVolcRtcEngine,
  isMobilePhoneUserAgent,
  retainVolcRtcEngine,
  VOLC_DEGRADED_VIDEO_ENCODER_CONFIG,
  VOLC_VIDEO_ENCODER_CONFIG,
} from './engine';
import { imageDataToDataUrl } from './image-data-to-data-url';
import {
  createMirroredCameraStream,
  hasMirroredCameraPlayback,
  pauseMirroredCameraPlayback,
  resolveMirrorCameraMaxSize,
  resumeMirroredCameraPlayback,
  stopMirroredCameraSource,
} from './mirror-camera-stream';
import { MuteAudioKeepalive, type VolcAudioMixingManagerLike } from './mute-audio-keepalive';
import { getRtcSupportStatus } from './rtc-support';
import { openRtcCameraStream, warmupRtcMicrophoneIfNeeded } from './warmup-rtc-microphone';
import { playWithWeChatUnlock } from './wechat-media-unlock';

export type AutoPlayBlockedKind = 'audio' | 'video';

export interface VolcRemoteBinding {
  uid: string;
  videoTrack: number;
  hasAudio: boolean;
}

const MEDIA_AUDIO = 1;
const MEDIA_VIDEO = 2;
const MEDIA_AUDIO_AND_VIDEO = 3;
const STREAM_INDEX_MAIN = 0;
const CAPTURE_VOLUME_MUTED = 0;
const CAPTURE_VOLUME_DEFAULT = 100;
const AUDIO_PROPERTIES_REPORT_INTERVAL_MS = 2_000;
const LOCAL_AUDIO_SPEAKING_VOLUME_THRESHOLD = 5;
const LOCAL_AUDIO_STALL_SAMPLE_THRESHOLD = 3;
const VOLC_REMOTE_VIDEO_TRACK_CAMERA = 1;
/** SDK 渲染模式：全部显示、可能留白，对齐现有 object-contain 观感（屏幕流默认值）。 */
const VIDEO_RENDER_MODE_FIT = 1;
/** SDK 渲染模式：视窗填满优先、等比缩放裁剪，对齐 object-cover 观感（H5 输出视图全屏用）。 */
const VIDEO_RENDER_MODE_HIDDEN = 0;
/** 上下行统计最多 10s 打一次，码率 0↔有或分辨率变化立刻打。 */
const DEV_STREAM_STATS_LOG_INTERVAL_MS = 10_000;

const CONNECTION_CONNECTED = 3;
const CONNECTION_RECONNECTING = 4;
const CONNECTION_RECONNECTED = 5;
const CONNECTION_DISCONNECTED = 1;
const CONNECTION_LOST = 6;
/** 上行质量好于该值时用高清档，差于 POOR 时用降级档；中间地带保持当前档形成滞回。 */
const GOOD_UPLINK_QUALITY = 2;
const POOR_UPLINK_QUALITY = 4;
const JOIN_RETRY_DELAYS_MS = [1_000, 2_000, 4_000] as const;
const TERMINAL_JOIN_ERROR_MARKERS = [
  'INVALID_TOKEN',
  'TOKEN_EXPIRED',
  'ROOM_FORBIDDEN',
  'USER_FORBIDDEN',
  'DUPLICATE_LOGIN',
  'KICKED_OUT',
  'ROOM_DISMISS',
] as const;

/**
 * 采集只限帧率。把横屏 960×540 的 max 套到 getUserMedia 上，竖屏前置会被塞进这个盒子，
 * Chrome 会走 crop-and-scale。分辨率由 setVideoEncoderConfig 在编码侧收敛。
 */
const CAMERA_CONSTRAINTS = {
  audio: false,
  video: {
    frameRate: VOLC_VIDEO_ENCODER_CONFIG.frameRate,
  },
} as const;

const SCREEN_SHARE_CONSTRAINTS = {
  frameRate: VOLC_VIDEO_ENCODER_CONFIG.frameRate,
} as const;

interface VolcRtcEngineLike {
  joinRoom: (
    token: string | null,
    roomId: string,
    userInfo: { userId: string },
    roomConfig?: {
      isAutoPublish?: boolean;
      isAutoSubscribeAudio?: boolean;
      isAutoSubscribeVideo?: boolean;
    },
  ) => Promise<unknown>;
  leaveRoom: (waitAck?: boolean) => Promise<unknown>;
  play: (userId?: string, mediaType?: number, streamIndex?: number) => Promise<unknown>;
  publishStream: (mediaType: number) => Promise<unknown>;
  setExternalVideoTrack: (index: number, track: MediaStreamTrack) => Promise<unknown>;
  setRemoteStreamRenderSync?: (sync: boolean) => boolean;
  setVideoEncoderConfig: (config: VideoEncoderConfig) => Promise<unknown>;
  setLocalVideoPlayer: (
    streamIndex: number,
    option?: { renderDom?: HTMLElement; renderMode?: number; userId?: string },
  ) => HTMLVideoElement | undefined;
  getLocalStreamTrack: (streamIndex: number, type: 'audio' | 'video') => MediaStreamTrack | undefined;
  setRemoteVideoPlayer: (
    streamIndex: number,
    option: { renderDom?: HTMLElement; renderMode?: number; userId: string },
  ) => HTMLVideoElement | undefined;
  getRemoteStreamTrack: (userId: string, streamIndex: number, type: 'audio' | 'video') => MediaStreamTrack | undefined;
  takeRemoteSnapshot?: (id: string, streamIndex: number) => Promise<ImageData>;
  enableAudioPropertiesReport: (config?: { enableInBackground?: boolean; interval?: number }) => void;
  getAudioMixingManager?: () => VolcAudioMixingManagerLike;
  setCaptureVolume?: (streamIndex: number, volume: number) => void;
  startAudioCapture: (deviceId?: string) => Promise<unknown>;
  stopAudioCapture: () => Promise<unknown>;
  subscribeStream: (userId: string, mediaType: number) => Promise<unknown>;
  unpublishStream: (mediaType: number) => Promise<unknown>;
  unsubscribeStream: (userId: string, mediaType: number) => Promise<unknown>;
  on: (event: string, handler: (...args: unknown[]) => void) => void;
  off: (event: string, handler: (...args: unknown[]) => void) => void;
}

interface VolcRtcSdkModule {
  default: {
    events: {
      onAutoplayFailed: string;
      onAudioDeviceStateChanged: string;
      onConnectionStateChanged: string;
      onError: string;
      onLocalAudioPropertiesReport: string;
      onLocalStatsException: string;
      onLocalStreamStats: string;
      onLocalVideoSizeChanged: string;
      onNetworkQuality: string;
      onRemoteStreamStats: string;
      onRemoteVideoFirstFrame: string;
      onPublishResult: string;
      onSubscribeResult: string;
      onTrackEnded: string;
      onUserPublishStream: string;
      onUserUnpublishStream: string;
    };
  };
}

interface VolcRemoteStats {
  audioStats?: {
    audioLossRate?: number;
    e2eDelay?: number;
    receivedKBitrate?: number;
    rtt?: number;
    statsInterval?: number;
  };
  isScreen?: boolean;
  userId: string;
  videoStats?: {
    decoderOutputFrameRate?: number;
    e2eDelay?: number;
    height?: number;
    receivedKBitrate?: number;
    rendererOutputFrameRate?: number;
    rtt?: number;
    statsInterval?: number;
    videoLossRate?: number;
    width?: number;
  };
}

export interface VolcRtcSessionParams {
  /** 入口权限预检得到的实际麦克风设备，避免 SDK 默认设备变化导致无声。 */
  audioCaptureDeviceId?: string;
  /** 入口已经完成过一次 getUserMedia({ audio: true })，RTC 阶段不再重复预热。 */
  microphonePreflightCompleted?: boolean;
  /** 为 false 时不采麦，也不发布音频。 */
  audioEnabled?: boolean;
  /** 兼容 live-call-session 入参。进房后始终先采麦再推视频，避免 Chrome 把 AEC 锁到高延迟路径。 */
  deferLocalAudioInitialization?: boolean;
  /** 为 false 时只订远端视频，不订下行音频。 */
  subscribeAudio?: boolean;
  remoteRenderSync?: boolean;
  /** 手机上是否允许从 540p/1200kbps 起播；弱网时仍可动态降到 270p。 */
  allowMobileHighUplink?: boolean;
  targetVideoFrameRate?: number;
  /**
   * 为 true 时远端画面改走 SDK 的 setRemoteVideoPlayer(renderDom) 渲染，而不是手动把 track 挂到页面 <video> 上。
   * 首帧仍听 onRemoteVideoFirstFrame；播放状态直接查 SDK 返回的 video element 的 paused，不再探测页面 DOM video。
   * 只影响远端渲染，本地预览路径不变。
   */
  useSdkRemotePlayer?: boolean;
  localVideoStream?: MediaStream | null;
  logger?: LiveDebugLogger;
  getEngine?: () => VolcRtcEngineLike | null;
  loadVolcSdk?: () => Promise<VolcRtcSdkModule>;
  onAutoPlayBlocked: (kind: AutoPlayBlockedKind) => void;
  onCameraError: (message: string | null) => void;
  onConnectionEstablished: () => void;
  onConnectionFailure: () => void;
  /** 浏览器缺少 WebRTC 基础能力时终止本次启动，不作为可重试的网络失败处理。 */
  onRtcUnsupported?: () => void;
  onConnectionRecovering?: () => void;
  onFatalError?: (message: string) => void;
  onLocalAudioEnabledChange: (enabled: boolean) => void;
  onLocalAudioRestoreError?: (message: string | null) => void;
  onLocalCameraEnabledChange?: (enabled: boolean) => void;
  onLocalScreenShareActiveChange?: (active: boolean) => void;
  onNetworkQualityChange?: (quality: { downlink: number; uplink: number }) => void;
  onLocalVideoStatsChange?: (stats: {
    sampledAtMs: number;
    sentFps?: number;
    sentKbps?: number;
    inputFps?: number;
  }) => void;
  onRemoteVideoFirstFrame?: () => void;
  onRemoteBindingsChange: (bindings: VolcRemoteBinding[]) => void;
  onRemoteMediaStatsChange: (stats: {
    audioE2eDelayMs?: number;
    sampledAtMs: number;
    uid: string | null;
    audioBytesReceived?: number;
    audioPacketsReceived?: number;
    hasAudioTrack?: boolean;
    hasVideoTrack?: boolean;
    subscribeLoss?: number;
    subscribeQuality?: number;
    subscribeRttMs?: number;
    videoBytesReceived?: number;
    videoDecodeFps?: number;
    videoFramesDecoded?: number;
    videoFramesReceived?: number;
    videoFramesRendered?: number;
    videoFrameHeight?: number;
    videoFrameWidth?: number;
    videoPacketsReceived?: number;
    videoE2eDelayMs?: number;
  }) => void;
  onRemoteTrackPresenceChange: (presence: { hasAudioTrack: boolean; hasVideoTrack: boolean; uid: string }) => void;
  onScreenShareError?: (message: string | null) => void;
  onScreenShareSupportedChange?: (supported: boolean) => void;
  onRtcJoinedChange?: (joined: boolean) => void;
}

interface StartOptions {
  appId?: string;
  roomId: string;
  rtcUserId: string;
  token: string;
}

const loadVolcRtcSdk = async (): Promise<VolcRtcSdkModule> => {
  return import('@volcengine/rtc') as Promise<VolcRtcSdkModule>;
};

const hasMediaType = (mediaType: number, expected: number): boolean => {
  return (mediaType & expected) === expected;
};

const isBrowserScreenShareSupported = (): boolean => {
  return typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getDisplayMedia === 'function';
};

interface DevStreamStatsFingerprint {
  audioKbps: number;
  videoFps?: number;
  height?: number;
  videoKbps: number;
  width?: number;
}

interface DevStreamStatsLogState {
  fingerprint: DevStreamStatsFingerprint | null;
  loggedAt: number;
}

const hasSignificantStreamStatsChange = (
  previous: DevStreamStatsFingerprint | null,
  next: DevStreamStatsFingerprint,
): boolean => {
  if (!previous) {
    return true;
  }
  return (
    (previous.audioKbps === 0) !== (next.audioKbps === 0) ||
    (previous.videoKbps === 0) !== (next.videoKbps === 0) ||
    (previous.videoFps === 0) !== (next.videoFps === 0) ||
    previous.width !== next.width ||
    previous.height !== next.height
  );
};

const shouldLogDevStreamStats = (
  state: DevStreamStatsLogState,
  fingerprint: DevStreamStatsFingerprint,
  now: number,
): boolean => {
  return (
    hasSignificantStreamStatsChange(state.fingerprint, fingerprint) ||
    now - state.loggedAt >= DEV_STREAM_STATS_LOG_INTERVAL_MS
  );
};

const normalizeError = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  return typeof error === 'string' ? error : JSON.stringify(error);
};

const normalizeRtcErrorCode = (error: unknown): string => {
  if (typeof error === 'string') {
    return error.toUpperCase();
  }
  if (error && typeof error === 'object') {
    const payload = error as { code?: unknown; errorCode?: unknown; message?: unknown; name?: unknown };
    const candidate = payload.errorCode ?? payload.code ?? payload.name ?? payload.message;
    if (typeof candidate === 'string') {
      return `${candidate} ${normalizeError(error)}`.toUpperCase();
    }
  }
  return normalizeError(error).toUpperCase();
};

const isTerminalJoinError = (error: unknown): boolean => {
  const code = normalizeRtcErrorCode(error);
  return (
    TERMINAL_JOIN_ERROR_MARKERS.some((marker) => code.includes(marker)) ||
    (code.includes('TOKEN') && (code.includes('INVALID') || code.includes('EXPIRED')))
  );
};

const isRecoverableJoinError = (error: unknown): boolean => {
  if (isTerminalJoinError(error)) {
    return false;
  }
  const code = normalizeRtcErrorCode(error);
  return code.includes('JOIN_ROOM_FAILED') || code.includes('NETWORK') || code.includes('TIMEOUT');
};

const pickDisplayBinding = (bindings: VolcRemoteBinding[]): VolcRemoteBinding | null => {
  return bindings.find((binding) => binding.videoTrack > 0) ?? bindings[0] ?? null;
};

export class VolcRtcSession {
  private audioCaptureDeviceId?: string;
  private readonly audioEnabled: boolean;
  private readonly subscribeAudio: boolean;
  private readonly remoteRenderSync: boolean;
  private readonly allowMobileHighUplink: boolean;
  private readonly targetVideoFrameRate: number | undefined;
  private readonly getEngine: () => VolcRtcEngineLike | null;
  private readonly loadVolcSdk: () => Promise<VolcRtcSdkModule>;
  private readonly microphonePreflightCompleted: boolean;
  private readonly logger?: LiveDebugLogger;
  private readonly onAutoPlayBlocked: VolcRtcSessionParams['onAutoPlayBlocked'];
  private readonly onCameraError: VolcRtcSessionParams['onCameraError'];
  private readonly onConnectionEstablished: VolcRtcSessionParams['onConnectionEstablished'];
  private readonly onConnectionFailure: VolcRtcSessionParams['onConnectionFailure'];
  private readonly onRtcUnsupported: NonNullable<VolcRtcSessionParams['onRtcUnsupported']>;
  private readonly onConnectionRecovering: NonNullable<VolcRtcSessionParams['onConnectionRecovering']>;
  private readonly onFatalError: NonNullable<VolcRtcSessionParams['onFatalError']>;
  private readonly onLocalAudioEnabledChange: VolcRtcSessionParams['onLocalAudioEnabledChange'];
  private readonly onLocalAudioRestoreError: NonNullable<VolcRtcSessionParams['onLocalAudioRestoreError']>;
  private readonly onLocalCameraEnabledChange: NonNullable<VolcRtcSessionParams['onLocalCameraEnabledChange']>;
  private readonly onLocalScreenShareActiveChange: NonNullable<VolcRtcSessionParams['onLocalScreenShareActiveChange']>;
  private readonly onNetworkQualityChange: NonNullable<VolcRtcSessionParams['onNetworkQualityChange']>;
  private readonly onLocalVideoStatsChange?: VolcRtcSessionParams['onLocalVideoStatsChange'];
  private readonly onRemoteVideoFirstFrame: NonNullable<VolcRtcSessionParams['onRemoteVideoFirstFrame']>;
  private readonly onRemoteBindingsChange: VolcRtcSessionParams['onRemoteBindingsChange'];
  private readonly onRemoteMediaStatsChange: VolcRtcSessionParams['onRemoteMediaStatsChange'];
  private readonly onRemoteTrackPresenceChange: VolcRtcSessionParams['onRemoteTrackPresenceChange'];
  private readonly onScreenShareError: NonNullable<VolcRtcSessionParams['onScreenShareError']>;
  private readonly onScreenShareSupportedChange: NonNullable<VolcRtcSessionParams['onScreenShareSupportedChange']>;
  private readonly onRtcJoinedChange: NonNullable<VolcRtcSessionParams['onRtcJoinedChange']>;
  private readonly retainsSharedEngine: boolean;

  private accumulatedAudioBytes = 0;
  private accumulatedAudioPackets = 0;
  private accumulatedVideoBytes = 0;
  private accumulatedVideoFramesDecoded = 0;
  private accumulatedVideoFramesReceived = 0;
  private accumulatedVideoFramesRendered = 0;
  private accumulatedVideoPackets = 0;
  private desiredLocalAudioEnabled = true;
  private desiredLocalCameraEnabled = false;
  private disposed = false;
  private isScreenShareActive = false;
  private engine: VolcRtcEngineLike | null = null;
  private eventDisposers: Array<() => void> = [];
  private hasJoined = false;
  private hasPublishedAudio = false;
  private hasPublishedVideo = false;
  private hasStartedAudioCapture = false;
  private currentContentHint: 'detail' | 'motion' | 'text' = 'motion';
  private currentEncoderProfile: 'degraded' | 'high' = 'high';
  /** 手机上行锁在 270p/600kbps，弱网恢复也不升回 540p。 */
  private capsUplinkAtDegraded = false;
  private lastLocalVideoSizeKey = '';
  private hasObservedVideoSent = false;
  private hasObservedRemoteFrame = false;
  private lastNetworkQualityKey = '';
  private lastConnectionState?: number;
  private lastDownlinkStatsLog: DevStreamStatsLogState = { fingerprint: null, loggedAt: 0 };
  private lastUplinkStatsLog: DevStreamStatsLogState = { fingerprint: null, loggedAt: 0 };
  private joinRetryResolve: (() => void) | null = null;
  private joinRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private localUserId = '';
  private localCapturePlaybackPaused = false;
  private localAudioStallSamples = 0;
  private lastLocalAudioLinearVolume = 0;
  private readonly muteAudioKeepalive = new MuteAudioKeepalive();
  private isRecoveringLocalAudio = false;
  private pendingAudioRecoveryDeviceId: string | undefined;
  private localVideoElement: HTMLVideoElement | null = null;
  private localVideoStream: MediaStream | null;
  private localAudioUpdateOperation: Promise<void> = Promise.resolve();
  private localVideoUpdateOperation: Promise<void> = Promise.resolve();
  private localCameraResumeOperation: Promise<void> = Promise.resolve();
  private screenShareStream: MediaStream | null = null;
  private remoteBindings: VolcRemoteBinding[] = [];
  private remoteMediaTypes = new Map<string, number>();
  private remoteVideoElement: HTMLVideoElement | null = null;
  private readonly useSdkRemotePlayer: boolean;
  private remoteRenderContainer: HTMLElement | null = null;
  private remoteSdkPlayerBoundUid: string | null = null;
  private remoteSdkVideoElement: HTMLVideoElement | null = null;
  private releaseEngineRetention: (() => void) | null = null;

  constructor({
    audioCaptureDeviceId,
    audioEnabled = true,
    deferLocalAudioInitialization: _deferLocalAudioInitialization = false,
    subscribeAudio = true,
    remoteRenderSync = false,
    allowMobileHighUplink = false,
    targetVideoFrameRate,
    useSdkRemotePlayer = false,
    localVideoStream = null,
    getEngine,
    loadVolcSdk = loadVolcRtcSdk,
    logger,
    microphonePreflightCompleted = false,
    onAutoPlayBlocked,
    onCameraError,
    onConnectionEstablished,
    onConnectionFailure,
    onRtcUnsupported = () => {},
    onConnectionRecovering = () => {},
    onFatalError = () => {},
    onLocalAudioEnabledChange,
    onLocalAudioRestoreError = () => {},
    onLocalCameraEnabledChange = () => {},
    onLocalScreenShareActiveChange = () => {},
    onNetworkQualityChange = () => {},
    onLocalVideoStatsChange,
    onRemoteVideoFirstFrame = () => {},
    onRemoteBindingsChange,
    onRemoteMediaStatsChange,
    onRemoteTrackPresenceChange,
    onScreenShareError = () => {},
    onScreenShareSupportedChange = () => {},
    onRtcJoinedChange = () => {},
  }: VolcRtcSessionParams) {
    this.audioCaptureDeviceId = audioCaptureDeviceId;
    this.audioEnabled = audioEnabled;
    this.subscribeAudio = subscribeAudio;
    this.remoteRenderSync = remoteRenderSync;
    this.allowMobileHighUplink = allowMobileHighUplink;
    this.targetVideoFrameRate = targetVideoFrameRate;
    this.useSdkRemotePlayer = useSdkRemotePlayer;
    this.localVideoStream = localVideoStream;
    this.getEngine = getEngine ?? (getVolcRtcEngine as () => VolcRtcEngineLike | null);
    this.retainsSharedEngine = getEngine === undefined;
    this.loadVolcSdk = loadVolcSdk;
    this.logger = logger;
    this.microphonePreflightCompleted = microphonePreflightCompleted;
    this.onAutoPlayBlocked = onAutoPlayBlocked;
    this.onCameraError = onCameraError;
    this.onConnectionEstablished = onConnectionEstablished;
    this.onConnectionFailure = onConnectionFailure;
    this.onRtcUnsupported = onRtcUnsupported;
    this.onConnectionRecovering = onConnectionRecovering;
    this.onFatalError = onFatalError;
    this.onLocalAudioEnabledChange = onLocalAudioEnabledChange;
    this.onLocalAudioRestoreError = onLocalAudioRestoreError;
    this.onLocalCameraEnabledChange = onLocalCameraEnabledChange;
    this.onLocalScreenShareActiveChange = onLocalScreenShareActiveChange;
    this.onNetworkQualityChange = onNetworkQualityChange;
    this.onLocalVideoStatsChange = onLocalVideoStatsChange;
    this.onRemoteVideoFirstFrame = onRemoteVideoFirstFrame;
    this.onRemoteBindingsChange = onRemoteBindingsChange;
    this.onRemoteMediaStatsChange = onRemoteMediaStatsChange;
    this.onRemoteTrackPresenceChange = onRemoteTrackPresenceChange;
    this.onScreenShareError = onScreenShareError;
    this.onScreenShareSupportedChange = onScreenShareSupportedChange;
    this.onRtcJoinedChange = onRtcJoinedChange;
  }

  bindLocalView(element: HTMLVideoElement | null): void {
    this.localVideoElement = element;
    this.applyLocalView();
  }

  bindRemoteView(element: HTMLVideoElement | null): void {
    this.remoteVideoElement = element;
    this.applyRemoteView();
  }

  /**
   * SDK 播放器模式专用：传入承载 SDK 内部 <video> 的父容器。
   * useSdkRemotePlayer 为 false 时不调用。
   */
  bindRemoteContainer(element: HTMLElement | null): void {
    this.remoteRenderContainer = element;
    this.applyRemoteView();
  }

  /**
   * 截图走 engine.takeRemoteSnapshot，不依赖页面 <video> 元素，两种渲染模式都可用。
   * 引擎未就绪、还没有可展示的远端画面或截图失败时返回 null。
   */
  async captureRemoteSnapshot(format: 'image/jpeg' | 'image/png' = 'image/jpeg'): Promise<string | null> {
    const engine = this.engine;
    const binding = pickDisplayBinding(this.remoteBindings);
    if (!engine || !binding || typeof engine.takeRemoteSnapshot !== 'function') {
      return null;
    }
    try {
      const imageData = await engine.takeRemoteSnapshot(binding.uid, STREAM_INDEX_MAIN);
      return imageDataToDataUrl(imageData, format);
    } catch {
      return null;
    }
  }

  async start({ appId, rtcUserId, token, roomId }: StartOptions): Promise<void> {
    if (this.disposed || this.hasJoined) {
      return;
    }

    if (!roomId || !rtcUserId || !token) {
      throw new Error('volc rtc join requires roomId, userId and token');
    }

    this.localUserId = rtcUserId;
    this.desiredLocalAudioEnabled = this.audioEnabled;
    this.capsUplinkAtDegraded =
      !this.allowMobileHighUplink && typeof navigator !== 'undefined' && isMobilePhoneUserAgent(navigator.userAgent);
    this.currentEncoderProfile = this.capsUplinkAtDegraded ? 'degraded' : 'high';
    this.onScreenShareSupportedChange(isBrowserScreenShareSupported());

    this.logger?.mark('rtc_start');
    this.logger?.log('rtc', 'rtc_start', {
      elapsed_ms_since_session_start: this.logger?.since('session_start'),
      vendor: 'volcengine',
    });

    const support = getRtcSupportStatus();
    if (!support.supported) {
      this.logger?.log('rtc', 'rtc_unsupported', {
        rtc_vendor: 'volcengine',
        ...support,
      });
      this.onRtcUnsupported();
      return;
    }

    if (this.retainsSharedEngine) {
      this.releaseEngineRetention ??= retainVolcRtcEngine();
    }
    const existingEngine = this.getEngine();
    const engine = existingEngine ?? ((await ensureVolcRtcEngine(appId ?? '')) as unknown as VolcRtcEngineLike);
    if (this.disposed) {
      return;
    }
    this.engine = engine;
    this.logger?.log('rtc', 'rtc_engine_ready');

    const sdk = await this.loadVolcSdk();
    if (this.disposed) {
      return;
    }

    this.bindEngineEvents(engine, sdk);
    this.logger?.log('rtc', 'rtc_sdk_ready');
    this.configureRemoteRenderSync(engine);

    const joined = await this.joinRoomWithRecovery(engine, { roomId, rtcUserId, token });
    if (!joined) {
      return;
    }
    this.hasJoined = true;
    this.onRtcJoinedChange(true);
    if (process.env.NODE_ENV === 'development') {
      console.info('[volc-rtc] joined', {
        has_local_video_stream: Boolean(this.localVideoStream),
        local_video_track_count: this.localVideoStream?.getVideoTracks().length ?? 0,
        local_video_track_state: this.localVideoStream?.getVideoTracks()[0]?.readyState,
      });
    }
    this.logger?.markOnce('rtc_join_channel_done');
    this.logger?.log('rtc', 'rtc_join_channel_done', {
      elapsed_ms_since_session_start: this.logger?.since('session_start'),
    });

    engine.enableAudioPropertiesReport({ interval: AUDIO_PROPERTIES_REPORT_INTERVAL_MS });

    if (this.audioEnabled) {
      try {
        await this.startLocalAudio();
      } catch (error: unknown) {
        this.onLocalAudioRestoreError(normalizeError(error));
      }
    } else {
      this.onLocalAudioEnabledChange(false);
    }

    if (this.localVideoStream) {
      await this.publishExternalVideo(this.localVideoStream);
    }
  }

  async stop(): Promise<void> {
    if (this.disposed) {
      return;
    }
    this.disposed = true;
    this.muteAudioKeepalive.stop();
    this.cancelJoinRetry();
    this.unbindEngineEvents();
    this.onRtcJoinedChange(false);
    this.isScreenShareActive = false;
    this.stopScreenShareTracks();
    await this.localAudioUpdateOperation.catch(() => {});
    await this.localVideoUpdateOperation.catch(() => {});

    const engine = this.engine;
    this.engine = null;
    if (!engine) {
      this.releaseLocalCameraPreview();
      this.clearRemoteState();
      this.releaseRetainedEngine();
      return;
    }

    try {
      engine.setCaptureVolume?.(STREAM_INDEX_MAIN, CAPTURE_VOLUME_DEFAULT);
    } catch {
      // 退房前恢复采集音量失败不能挡住 leaveRoom。
    }

    try {
      if (this.hasPublishedVideo || this.hasPublishedAudio) {
        await engine.unpublishStream(MEDIA_AUDIO_AND_VIDEO);
      }
    } catch {
      // 退房前取消发布失败不能挡住 leaveRoom。
    }

    await this.unsubscribeAllRemotes(engine);

    try {
      if (this.hasStartedAudioCapture) {
        await engine.stopAudioCapture();
      }
    } catch {
      // 停采集失败同样不能挡住退房。
    }

    this.releaseLocalCameraPreview();
    this.clearRemoteState();

    try {
      if (this.hasJoined) {
        await engine.leaveRoom();
      }
    } finally {
      this.hasJoined = false;
      this.hasPublishedAudio = false;
      this.hasPublishedVideo = false;
      this.hasStartedAudioCapture = false;
      this.releaseRetainedEngine();
    }
  }

  async toggleLocalAudio(currentValue: boolean): Promise<void> {
    if (!this.audioEnabled) {
      this.onLocalAudioEnabledChange(false);
      return;
    }
    const nextValue = !currentValue;
    this.desiredLocalAudioEnabled = nextValue;
    const operation = this.localAudioUpdateOperation.then(async () => {
      const engine = this.engine;
      if (!engine || !this.hasJoined || this.disposed) {
        this.onLocalAudioEnabledChange(false);
        return;
      }

      try {
        if (nextValue) {
          await this.startLocalAudio(true);
        } else {
          await this.applyMutedAudioKeepalive();
        }
      } catch (error) {
        this.desiredLocalAudioEnabled = false;
        this.onLocalAudioEnabledChange(false);
        this.onLocalAudioRestoreError(normalizeError(error));
        throw error;
      }
    });
    this.localAudioUpdateOperation = operation.catch(() => {});
    return operation;
  }

  updateLocalVideoStream(stream: MediaStream | null): Promise<void> {
    const operation = this.localVideoUpdateOperation.then(() => this.performLocalVideoStreamUpdate(stream));
    this.localVideoUpdateOperation = operation.catch(() => {});
    return operation;
  }

  private async performLocalVideoStreamUpdate(stream: MediaStream | null): Promise<void> {
    if (this.disposed) {
      return;
    }
    const previousStream = this.localVideoStream;
    const previousTrack = previousStream?.getVideoTracks()[0];
    const nextTrack = stream?.getVideoTracks()[0];
    this.localVideoStream = stream;
    if (!stream) {
      await this.unpublishVideo();
      return;
    }
    this.applyLocalView();
    if (!this.engine || !this.hasJoined) {
      return;
    }
    try {
      await this.publishExternalVideo(stream, {
        replacePublishedTrack: Boolean(previousTrack && nextTrack && previousTrack !== nextTrack),
      });
      if (this.desiredLocalAudioEnabled) {
        await this.startLocalAudio();
      }
    } catch (error) {
      if (!this.disposed) {
        this.localVideoStream = previousStream;
        this.applyLocalView();
        if (previousStream && !this.hasPublishedVideo && previousTrack?.readyState !== 'ended') {
          try {
            await this.publishExternalVideo(previousStream);
          } catch (restoreError) {
            this.logger?.log('rtc', 'rtc_restore_previous_uplink_failed', {
              error_message: sanitizeString(normalizeError(restoreError)),
            });
          }
        }
      }
      throw error;
    }
  }

  async toggleLocalCamera(currentValue: boolean, cameraPermissionErrorText: string): Promise<void> {
    const nextValue = !currentValue;
    this.desiredLocalCameraEnabled = nextValue;
    const engine = this.engine;
    if (!engine || !this.hasJoined) {
      this.onLocalCameraEnabledChange(nextValue);
      return;
    }

    if (!nextValue) {
      this.stopLocalCameraTracks();
      this.localVideoStream = null;
      this.applyLocalView();
      // 共享占用同一路上行时不能把 screen 一起 unpublished，只停摄像头采集和预览。
      if (!this.isScreenShareActive) {
        await this.unpublishVideo();
      }
      this.onLocalCameraEnabledChange(false);
      return;
    }

    try {
      await this.startLocalCamera();
    } catch (error) {
      this.desiredLocalCameraEnabled = false;
      this.onCameraError(cameraPermissionErrorText || normalizeError(error));
      this.onLocalCameraEnabledChange(false);
    }
  }

  async toggleLocalScreenShare(
    currentValue: boolean,
    options: { permissionErrorText: string; startErrorText: string; unsupportedErrorText: string },
  ): Promise<void> {
    const engine = this.engine;
    if (!engine || !this.hasJoined) {
      return;
    }

    this.onScreenShareError(null);
    if (currentValue) {
      await this.stopScreenShareAndRestoreCamera();
      return;
    }

    if (!isBrowserScreenShareSupported()) {
      this.onScreenShareSupportedChange(false);
      this.onScreenShareError(options.unsupportedErrorText);
      return;
    }

    let capturedStream: MediaStream | null = null;
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        audio: false,
        video: SCREEN_SHARE_CONSTRAINTS,
      });
      capturedStream = stream;
      const [track] = stream.getVideoTracks();
      if (!track) {
        stream.getTracks().forEach((item) => {
          item.stop();
        });
        this.onScreenShareError(options.startErrorText);
        return;
      }
      track.addEventListener(
        'ended',
        () => {
          void this.stopScreenShareAndRestoreCamera();
        },
        { once: true },
      );
      this.screenShareStream = stream;
      this.isScreenShareActive = true;
      await this.publishExternalVideo(stream, {
        replacePublishedTrack: this.hasPublishedVideo,
      });
      this.onLocalScreenShareActiveChange(true);
    } catch {
      this.isScreenShareActive = false;
      this.screenShareStream = null;
      capturedStream?.getTracks().forEach((item) => {
        if (item.readyState !== 'ended') {
          item.stop();
        }
      });
      this.onScreenShareError(options.startErrorText);
      if (this.desiredLocalCameraEnabled && this.hasUsableLocalCameraStream() && this.localVideoStream) {
        try {
          await this.publishExternalVideo(this.localVideoStream, {
            replacePublishedTrack: this.hasPublishedVideo,
          });
        } catch {
          // 共享启动失败后尽量把摄像头上行抢回来，失败也不覆盖屏幕共享错误。
        }
      }
    }
  }

  async restoreLocalMedia(): Promise<void> {
    if (!this.engine || !this.hasJoined) {
      return;
    }
    if (this.desiredLocalAudioEnabled) {
      await this.startLocalAudio();
    } else if (this.audioEnabled) {
      await this.applyMutedAudioKeepalive({ ensureUplink: true });
    }
    if (this.isScreenShareActive || this.screenShareStream) {
      // getDisplayMedia 依赖用户手势，重连后无法静默恢复屏幕共享，只把摄像头意图还原。
      await this.stopScreenShareAndRestoreCamera();
      return;
    }
    if (this.desiredLocalCameraEnabled && !this.hasUsableLocalCameraStream()) {
      await this.startLocalCamera();
      return;
    }
    if (this.localVideoStream) {
      await this.publishExternalVideo(this.localVideoStream);
    }
  }

  resumePlayback(): Promise<void> {
    const binding = pickDisplayBinding(this.remoteBindings);
    if (this.useSdkRemotePlayer) {
      const videoPlay =
        binding && binding.videoTrack > 0
          ? Promise.resolve(this.engine?.play(binding.uid, MEDIA_VIDEO, STREAM_INDEX_MAIN) ?? Promise.resolve()).catch(
              () => undefined,
            )
          : Promise.resolve();
      const remoteUserId = this.subscribeAudio && binding?.hasAudio ? binding.uid : undefined;
      const audioPlay = remoteUserId
        ? Promise.resolve(this.engine?.play(remoteUserId, MEDIA_AUDIO, STREAM_INDEX_MAIN) ?? Promise.resolve())
        : Promise.resolve();
      return Promise.all([videoPlay, audioPlay]).then(() => undefined);
    }
    const element = this.remoteVideoElement;
    if (element) {
      element.muted = true;
    }
    const videoPlay = Promise.resolve(element?.play() ?? Promise.resolve()).catch(() => undefined);
    const remoteUserId = this.subscribeAudio && binding?.hasAudio ? binding.uid : undefined;
    const audioPlay = remoteUserId
      ? Promise.resolve(this.engine?.play(remoteUserId, MEDIA_AUDIO, STREAM_INDEX_MAIN) ?? Promise.resolve())
      : Promise.resolve();
    return Promise.all([videoPlay, audioPlay]).then(() => undefined);
  }

  resumeRemoteView(): void {
    this.applyRemoteView();
  }

  /**
   * 回前台恢复本端摄像头预览。
   *
   * 微信小程序 webview 切后台会停采集；镜像 canvas 流即使轨道还 live 也是黑的。
   * 连接通常还活着，所以不能指望 restoreLocalMedia / 重连。
   */
  resumeLocalCamera(): Promise<void> {
    const operation = this.localCameraResumeOperation.then(() => this.performLocalCameraResume());
    this.localCameraResumeOperation = operation.catch(() => {});
    return operation;
  }

  private async performLocalCameraResume(): Promise<void> {
    if (this.disposed || this.isScreenShareActive) {
      return;
    }
    if (!this.desiredLocalCameraEnabled) {
      this.applyLocalView();
      return;
    }
    if (!this.engine || !this.hasJoined) {
      this.applyLocalView();
      return;
    }
    if (shouldReacquireCameraOnForeground(this.localVideoStream)) {
      try {
        await this.startLocalCamera();
      } catch (error) {
        this.logger?.log('rtc', 'rtc_resume_local_camera_failed', {
          error_message: sanitizeString(normalizeError(error)),
        });
        this.applyLocalView();
      }
      return;
    }
    this.applyLocalView();
  }

  private async startLocalCamera(): Promise<void> {
    const captured = await openRtcCameraStream(
      this.targetVideoFrameRate
        ? { frameRate: { ideal: this.targetVideoFrameRate, max: this.targetVideoFrameRate } }
        : CAMERA_CONSTRAINTS.video,
      undefined,
      { skipWarmup: true },
    );
    if (!captured) {
      return;
    }
    if (this.disposed || !this.desiredLocalCameraEnabled) {
      captured.getTracks().forEach((track) => {
        track.stop();
      });
      return;
    }
    const stream = await createMirroredCameraStream(captured, {
      ...resolveMirrorCameraMaxSize(),
      targetFrameRate: this.targetVideoFrameRate,
    });
    if (this.disposed || !this.desiredLocalCameraEnabled) {
      stream.getTracks().forEach((track) => {
        track.stop();
      });
      if (stream !== captured) {
        captured.getTracks().forEach((track) => {
          track.stop();
        });
      }
      return;
    }
    this.stopLocalCameraTracks();
    this.localVideoStream = stream;
    this.applyLocalView();
    if (!this.isScreenShareActive) {
      await this.publishExternalVideo(stream, {
        replacePublishedTrack: this.hasPublishedVideo,
      });
    }
    this.onLocalCameraEnabledChange(true);
  }

  private async startLocalAudio(ignoreDesiredState = false): Promise<void> {
    const ready = await this.ensureLocalAudioUplink(ignoreDesiredState);
    if (!ready || this.disposed) {
      return;
    }
    this.muteAudioKeepalive.stop();
    this.engine?.setCaptureVolume?.(STREAM_INDEX_MAIN, CAPTURE_VOLUME_DEFAULT);
    this.onLocalAudioEnabledChange(true);
    this.onLocalAudioRestoreError(null);
  }

  private async applyMutedAudioKeepalive(options: { ensureUplink?: boolean } = {}): Promise<void> {
    const ready = options.ensureUplink
      ? await this.ensureLocalAudioUplink(true)
      : this.hasStartedAudioCapture && this.hasPublishedAudio;
    if (!ready || this.disposed) {
      this.onLocalAudioEnabledChange(false);
      return;
    }
    this.engine?.setCaptureVolume?.(STREAM_INDEX_MAIN, CAPTURE_VOLUME_MUTED);
    try {
      this.muteAudioKeepalive.start(this.engine?.getAudioMixingManager?.());
    } catch (error) {
      this.logger?.log('rtc', 'rtc_mute_keepalive_failed', {
        error_message: sanitizeString(normalizeError(error)),
      });
    }
    this.onLocalAudioEnabledChange(false);
  }

  private async ensureLocalAudioUplink(ignoreDesiredState = false): Promise<boolean> {
    const engine = this.engine;
    if (!engine || !this.hasJoined || (!ignoreDesiredState && !this.desiredLocalAudioEnabled)) {
      return false;
    }
    const shouldRestoreCameraAfterCapture = !this.hasStartedAudioCapture && this.shouldStopCameraBeforeAudioCapture();
    if (!this.hasStartedAudioCapture) {
      this.pauseLocalCapturePlayback();
      if (shouldRestoreCameraAfterCapture) {
        this.stopCameraCaptureForAudio();
      }
      try {
        if (!this.microphonePreflightCompleted) {
          await warmupRtcMicrophoneIfNeeded();
        }
        if (this.disposed || this.engine !== engine) {
          return false;
        }
        await engine.startAudioCapture(this.audioCaptureDeviceId);
        if (this.disposed || this.engine !== engine) {
          try {
            await engine.stopAudioCapture();
          } catch {
            // 关闭过程中采集才完成时补一次停止，避免麦克风在结束页继续亮着。
          }
          return false;
        }
        this.hasStartedAudioCapture = true;
      } finally {
        this.resumeLocalCapturePlayback();
        // 采麦失败时也要把编辑页摄像头抢回来，拒绝授权不能把画面停掉。
        if (
          shouldRestoreCameraAfterCapture &&
          !this.hasStartedAudioCapture &&
          !this.disposed &&
          this.engine === engine
        ) {
          this.desiredLocalCameraEnabled = true;
          try {
            await this.startLocalCamera();
          } catch {
            // 摄像头恢复失败不覆盖麦克风错误，用户可再点一次打开摄像头。
          }
        }
      }
    }
    if (this.disposed || this.engine !== engine) {
      return false;
    }
    if (!this.hasPublishedAudio) {
      await engine.publishStream(MEDIA_AUDIO);
      this.hasPublishedAudio = true;
    }
    // 必须先发布音频再 getUserMedia 重开摄像头。采麦后立刻再采画面会把 SDK 刚建的
    // 音频会话抢走，远端 <video> 直接没声；/live-pro 是进房出声之后才开摄像头。
    if (shouldRestoreCameraAfterCapture && !this.disposed && this.engine === engine) {
      this.desiredLocalCameraEnabled = true;
      try {
        await this.startLocalCamera();
      } catch {
        // 采麦后重开摄像头失败时保留已建立的音频，用户可再点一次打开摄像头。
      }
    }
    return this.hasPublishedAudio && this.hasStartedAudioCapture;
  }

  private async stopLocalAudio({
    clearError = true,
    stopCapture = true,
  }: {
    clearError?: boolean;
    stopCapture?: boolean;
  } = {}): Promise<void> {
    const engine = this.engine;
    if (!engine || !this.hasJoined) {
      return;
    }

    this.muteAudioKeepalive.stop();
    engine.setCaptureVolume?.(STREAM_INDEX_MAIN, CAPTURE_VOLUME_DEFAULT);

    try {
      if (this.hasPublishedAudio) {
        await engine.unpublishStream(MEDIA_AUDIO);
        this.hasPublishedAudio = false;
      }
    } finally {
      if (stopCapture && this.hasStartedAudioCapture) {
        await engine.stopAudioCapture();
        this.hasStartedAudioCapture = false;
      }
    }
    this.onLocalAudioEnabledChange(false);
    if (clearError) {
      this.onLocalAudioRestoreError(null);
    }
  }

  private queueLocalAudioRecovery(reason: string, deviceId?: string): void {
    if (this.disposed || !this.desiredLocalAudioEnabled || this.isRecoveringLocalAudio) {
      return;
    }
    if (deviceId) {
      this.audioCaptureDeviceId = deviceId;
    }
    this.isRecoveringLocalAudio = true;
    const operation = this.localAudioUpdateOperation.then(async () => {
      if (this.disposed || !this.engine || !this.hasJoined || !this.desiredLocalAudioEnabled) {
        return;
      }
      this.logger?.log('rtc', 'rtc_local_audio_recovery_start', {
        reason,
      });
      try {
        await this.stopLocalAudio({ clearError: false });
        await this.startLocalAudio(true);
        this.localAudioStallSamples = 0;
        this.logger?.log('rtc', 'rtc_local_audio_recovery_done', {
          reason,
          success: true,
        });
      } catch (error) {
        this.onLocalAudioRestoreError(normalizeError(error));
        this.logger?.log('rtc', 'rtc_local_audio_recovery_done', {
          error_message: sanitizeString(normalizeError(error)),
          reason,
          success: false,
        });
      } finally {
        this.isRecoveringLocalAudio = false;
        const pendingDeviceId = this.pendingAudioRecoveryDeviceId;
        this.pendingAudioRecoveryDeviceId = undefined;
        if (pendingDeviceId) {
          this.queueLocalAudioRecovery('audio_device_connected', pendingDeviceId);
        }
      }
    });
    this.localAudioUpdateOperation = operation.catch(() => {});
  }

  private queueLocalAudioSuspension(reason: string): void {
    if (this.disposed || this.isRecoveringLocalAudio) {
      return;
    }
    this.isRecoveringLocalAudio = true;
    const operation = this.localAudioUpdateOperation
      .then(async () => {
        if (this.disposed) {
          return;
        }
        await this.stopLocalAudio({ clearError: false });
        this.onLocalAudioRestoreError(reason);
      })
      .finally(() => {
        this.isRecoveringLocalAudio = false;
        const pendingDeviceId = this.pendingAudioRecoveryDeviceId;
        this.pendingAudioRecoveryDeviceId = undefined;
        if (pendingDeviceId) {
          this.queueLocalAudioRecovery('audio_device_connected', pendingDeviceId);
        }
      });
    this.localAudioUpdateOperation = operation.catch(() => {});
  }

  private async publishExternalVideo(
    stream: MediaStream,
    options: { replacePublishedTrack?: boolean } = {},
  ): Promise<void> {
    if (this.disposed) {
      return;
    }
    const engine = this.engine;
    const [track] = stream.getVideoTracks();
    if (!engine || !track) {
      // 上行完全没有帧时，最常见的原因就是这里被静默跳过；必须留下可诊断的记录。
      this.logger?.log('rtc', 'rtc_publish_skipped', {
        has_engine: Boolean(engine),
        has_joined: this.hasJoined,
        video_track_count: stream.getVideoTracks().length,
      });
      if (process.env.NODE_ENV === 'development') {
        console.warn('[volc-rtc] uplink-skipped', {
          has_engine: Boolean(engine),
          has_joined: this.hasJoined,
          video_track_count: stream.getVideoTracks().length,
        });
      }
      return;
    }

    // 已结束的轨道交给 SDK 会抛 "The track is not connected to any source"，
    // 这种情况通常是上一场直播的轨道被复用，必须在这里拦住并给出可定位的错误。
    if (track.readyState === 'ended') {
      this.logger?.log('rtc', 'rtc_publish_rejected_ended_track', {
        track_id: track.id,
      });
      if (this.disposed) {
        return;
      }
      throw new Error('volc-rtc local video track ended');
    }

    this.currentContentHint =
      !this.targetVideoFrameRate && (track.contentHint === 'detail' || track.contentHint === 'text')
        ? track.contentHint
        : 'motion';
    const trackSettings = track.getSettings?.();
    this.logger?.log('rtc', 'rtc_local_video_track', {
      ready_state: track.readyState,
      enabled: track.enabled,
      muted: track.muted,
      width: trackSettings?.width,
      height: trackSettings?.height,
      frame_rate: trackSettings?.frameRate,
    });
    if (options.replacePublishedTrack && this.hasPublishedVideo) {
      await this.runPublishStage('unpublish_stream', () => engine.unpublishStream(MEDIA_VIDEO));
      this.hasPublishedVideo = false;
      if (this.disposed || this.engine !== engine) {
        return;
      }
    }
    // 顺序不能反：引擎是页面级单例，setVideoEncoderConfig 会作用在 MAIN 当前的外部轨上。
    // 上一场结束后那条轨已经停止，先设编码参数会命中失效轨并抛
    // "The track is not connected to any source"。
    await this.runPublishStage('set_external_video_track', () =>
      engine.setExternalVideoTrack(STREAM_INDEX_MAIN, track),
    );
    if (this.disposed || this.engine !== engine) {
      return;
    }
    await this.runPublishStage('set_video_encoder_config', () =>
      engine.setVideoEncoderConfig({
        ...this.resolveEncoderConfig(),
        contentHint: this.currentContentHint,
      }),
    );
    if (this.disposed || this.engine !== engine) {
      return;
    }
    this.applyLocalView();
    if (!this.hasPublishedVideo) {
      await this.runPublishStage('publish_stream', () => engine.publishStream(MEDIA_VIDEO));
      this.hasPublishedVideo = true;
    }
    if (process.env.NODE_ENV === 'development') {
      console.info('[volc-rtc] uplink-track-applied', {
        content_hint: this.currentContentHint,
        muted: track.muted,
        published: this.hasPublishedVideo,
        ready_state: track.readyState,
        settings: track.getSettings?.() ?? null,
      });
    }
    this.logger?.markOnce('rtc_publish_called');
    this.logger?.logOnce('rtc_publish_called', 'rtc', 'rtc_publish_called', {
      elapsed_ms_since_rtc_join_channel_done: this.logger?.since('rtc_join_channel_done'),
      elapsed_ms_since_session_start: this.logger?.since('session_start'),
      vendor: 'volcengine',
    });
  }

  private async unpublishVideo(): Promise<void> {
    const engine = this.engine;
    if (!engine || !this.hasPublishedVideo) {
      return;
    }
    await engine.unpublishStream(MEDIA_VIDEO);
    this.hasPublishedVideo = false;
  }

  private configureRemoteRenderSync(engine: VolcRtcEngineLike): void {
    try {
      engine.setRemoteStreamRenderSync?.(this.remoteRenderSync);
    } catch {
      // 老版本 SDK 没有这个能力时保持默认同步渲染。
    }
  }

  /** 发布链路的每个 SDK 调用都标注阶段，失败时错误信息能直接定位到具体调用。 */
  private async runPublishStage(stage: string, call: () => Promise<unknown>): Promise<void> {
    const startedAt = performance.now();
    this.logger?.log('rtc', 'rtc_publish_stage_start', { stage });
    try {
      await call();
      this.logger?.log('rtc', 'rtc_publish_stage_done', { stage, elapsed_ms: performance.now() - startedAt });
    } catch (error) {
      const message = normalizeError(error);
      this.logger?.log('rtc', 'rtc_publish_stage_failed', {
        error_message: sanitizeString(message),
        stage,
      });
      if (process.env.NODE_ENV === 'development') {
        console.error('[volc-rtc] publish-stage-failed', { error: message, stage });
      }
      throw new Error(`${stage}: ${message}`);
    }
  }

  private stopLocalCameraTracks(): void {
    this.localVideoStream?.getTracks().forEach((track) => {
      if (track.readyState !== 'ended') {
        track.stop();
      }
    });
  }

  /** 停掉本端摄像头采集并清预览。结束页不卸载，不能把这件事留给页面级 destroyEngine。 */
  private releaseLocalCameraPreview(): void {
    this.stopLocalCameraTracks();
    this.localVideoStream = null;
    if (this.localVideoElement) {
      this.localVideoElement.srcObject = null;
    }
    this.desiredLocalCameraEnabled = false;
    this.onLocalCameraEnabledChange(false);
  }

  private hasUsableLocalCameraStream(): boolean {
    return hasUsableCameraVideoTrack(this.localVideoStream);
  }

  private stopScreenShareTracks(): void {
    this.screenShareStream?.getTracks().forEach((track) => {
      if (track.readyState !== 'ended') {
        track.stop();
      }
    });
    this.screenShareStream = null;
  }

  /**
   * 火山只有一路视频上行：关共享后若摄像头仍是开启意图，把 camera 轨重新挂回去。
   * 不能走 unpublish 后把摄像头 UI 一起关掉，也不能把摄像头采集流丢掉。
   */
  private async stopScreenShareAndRestoreCamera(): Promise<void> {
    if (!this.isScreenShareActive && !this.screenShareStream) {
      return;
    }

    this.isScreenShareActive = false;
    this.stopScreenShareTracks();
    this.onLocalScreenShareActiveChange(false);

    if (this.desiredLocalCameraEnabled && this.hasUsableLocalCameraStream() && this.localVideoStream) {
      await this.publishExternalVideo(this.localVideoStream, {
        replacePublishedTrack: this.hasPublishedVideo,
      });
      this.applyLocalView();
      return;
    }

    await this.unpublishVideo();
    this.applyLocalView();
  }

  private applyLocalView(): void {
    const element = this.localVideoElement;
    if (!element) {
      return;
    }

    const inputStream = this.localVideoStream;
    const engineTrack = this.engine?.getLocalStreamTrack(STREAM_INDEX_MAIN, 'video');
    const previewStream = inputStream ?? (engineTrack ? new MediaStream([engineTrack]) : null);
    if (!previewStream) {
      element.srcObject = null;
      return;
    }

    if (element.srcObject !== previewStream) {
      element.srcObject = previewStream;
    }
    if (this.localCapturePlaybackPaused) {
      return;
    }
    void playWithWeChatUnlock(() => element.play() ?? Promise.resolve()).catch(() => {});
  }

  /**
   * pause 本地 video 不够：Chrome AEC 看的是还在采集的 GUM 摄像头轨。
   * /live-pro 进房时没有这条轨；编辑页必须先停采集再 startAudioCapture。
   */
  private shouldStopCameraBeforeAudioCapture(): boolean {
    if (this.isScreenShareActive || this.screenShareStream === this.localVideoStream) {
      return false;
    }
    return hasMirroredCameraPlayback(this.localVideoStream) || isRtcCameraGumStream(this.localVideoStream);
  }

  private stopCameraCaptureForAudio(): void {
    if (stopMirroredCameraSource(this.localVideoStream)) {
      return;
    }
    this.stopLocalCameraTracks();
    this.localVideoStream = null;
    if (this.localVideoElement) {
      this.localVideoElement.srcObject = null;
    }
  }

  private pauseLocalCapturePlayback(): void {
    this.localCapturePlaybackPaused = true;
    pauseMirroredCameraPlayback(this.localVideoStream);
    const videos = new Set<HTMLVideoElement>();
    if (this.localVideoElement) {
      videos.add(this.localVideoElement);
    }
    if (typeof document !== 'undefined' && typeof document.querySelectorAll === 'function' && this.localVideoStream) {
      for (const video of document.querySelectorAll('video')) {
        if (video.srcObject === this.localVideoStream) {
          videos.add(video);
        }
      }
    }
    for (const video of videos) {
      if (video.paused) {
        continue;
      }
      try {
        video.pause();
      } catch {
        // jsdom 和部分 WebView 的 pause 会抛未实现错误。
      }
    }
  }

  private resumeLocalCapturePlayback(): void {
    if (!this.localCapturePlaybackPaused) {
      return;
    }
    this.localCapturePlaybackPaused = false;
    resumeMirroredCameraPlayback(this.localVideoStream);
    this.applyLocalView();
  }

  private applyRemoteView(): void {
    if (this.useSdkRemotePlayer) {
      this.applyRemoteViewSdk();
      return;
    }
    const engine = this.engine;
    const element = this.remoteVideoElement;
    const binding = pickDisplayBinding(this.remoteBindings);
    if (!engine || !element) {
      return;
    }
    if (!binding) {
      element.srcObject = null;
      return;
    }

    // 火山 setRemoteVideoPlayer 的 renderDom 是父容器，会自己再插一个 video。
    // 会话层首帧检测听的是页面上这个 video，必须把订阅到的 video track 直接挂上去。
    // 声音只走 SDK 内部 <audio>（AUTO_PLAY），页面 video 不挂音轨，避免和 SDK 叠成回声。
    const videoTrack =
      binding.videoTrack > 0 ? engine.getRemoteStreamTrack(binding.uid, STREAM_INDEX_MAIN, 'video') : undefined;
    const tracks = [videoTrack].filter((track): track is MediaStreamTrack => Boolean(track));
    if (tracks.length === 0) {
      return;
    }

    const current = element.srcObject;
    const currentTracks = current instanceof MediaStream ? current.getTracks() : [];
    const sameTracks = currentTracks.length === tracks.length && tracks.every((track) => currentTracks.includes(track));
    // 对齐 /live：绑定没变且已在播就不要再 mute/play。
    if (sameTracks && !element.paused) {
      return;
    }
    element.muted = true;
    if (!sameTracks) {
      element.srcObject = new MediaStream(tracks);
    }
    // 对齐 /live：play() 被拒只说明这次调用没要到手势，不代表画面真的没在播。
    // 微信静音 autoplay 经常 NotAllowedError，但 <video autoPlay> 仍在出画。
    // 真拦截交给 SDK onAutoplayFailed，上层再按有没有音频包决定是否弹提示。
    void Promise.resolve(element.play() ?? Promise.resolve()).catch(() => undefined);
  }

  /**
   * SDK 播放器模式：直接把远端流绑给 setRemoteVideoPlayer(renderDom)，渲染、首帧检测全交给 SDK。
   * 只在 useSdkRemotePlayer 为 true 时调用。
   */
  private applyRemoteViewSdk(): void {
    const engine = this.engine;
    const container = this.remoteRenderContainer;
    const binding = pickDisplayBinding(this.remoteBindings);
    const targetUid = container && binding && binding.videoTrack > 0 ? binding.uid : null;

    if (this.remoteSdkPlayerBoundUid && this.remoteSdkPlayerBoundUid !== targetUid) {
      this.unbindSdkRemotePlayer(this.remoteSdkPlayerBoundUid);
      this.remoteSdkPlayerBoundUid = null;
      this.remoteSdkVideoElement = null;
    }

    if (!engine || !targetUid || this.remoteSdkPlayerBoundUid === targetUid) {
      return;
    }

    // H5（手机 UA）输出视图要全屏铺满，远端画面用 HIDDEN（cover）等比裁剪填满容器；桌面仍保持 FIT 完整呈现。
    const renderMode =
      typeof navigator !== 'undefined' && isMobilePhoneUserAgent(navigator.userAgent)
        ? VIDEO_RENDER_MODE_HIDDEN
        : VIDEO_RENDER_MODE_FIT;
    const videoElement = engine.setRemoteVideoPlayer(STREAM_INDEX_MAIN, {
      renderDom: container ?? undefined,
      renderMode,
      userId: targetUid,
    });
    this.remoteSdkPlayerBoundUid = targetUid;
    this.remoteSdkVideoElement = videoElement ?? null;
  }

  private unbindSdkRemotePlayer(uid: string): void {
    const engine = this.engine;
    if (!engine) {
      return;
    }
    // 再次调用并绑定空视图（不传 renderDom）解除该 uid 的默认播放器绑定。
    engine.setRemoteVideoPlayer(STREAM_INDEX_MAIN, { userId: uid });
  }

  private isRemotePlaybackActive(kind?: AutoPlayBlockedKind): boolean {
    const element = this.useSdkRemotePlayer ? this.remoteSdkVideoElement : this.remoteVideoElement;
    if (!element || element.paused) {
      return false;
    }
    // 页面 video 只出画面。SDK 音频失败不能因为画面在播就忽略。
    if (kind === 'audio') {
      return false;
    }
    return true;
  }

  private reportAutoPlayBlocked(kind: AutoPlayBlockedKind): void {
    if (this.isRemotePlaybackActive(kind)) {
      return;
    }
    this.logger?.log('rtc', 'rtc_autoplay_blocked', { kind });
    this.onAutoPlayBlocked(kind);
  }

  private async joinRoomWithRecovery(engine: VolcRtcEngineLike, options: StartOptions): Promise<boolean> {
    for (let attempt = 0; attempt <= JOIN_RETRY_DELAYS_MS.length; attempt += 1) {
      if (this.disposed || this.engine !== engine) {
        return false;
      }
      this.logger?.log('rtc', 'rtc_join_requested', {
        attempt: attempt + 1,
        room_id: options.roomId,
        user_id: options.rtcUserId,
      });
      try {
        await engine.joinRoom(
          options.token,
          options.roomId,
          { userId: options.rtcUserId },
          {
            isAutoPublish: false,
            isAutoSubscribeAudio: false,
            isAutoSubscribeVideo: false,
          },
        );
        if (this.disposed || this.engine !== engine) {
          await this.leaveRoomBestEffort(engine);
          return false;
        }
        return true;
      } catch (error) {
        this.logger?.log('rtc', 'rtc_join_failed', {
          attempt: attempt + 1,
          error_message: sanitizeString(normalizeError(error)),
        });
        const retryDelay = JOIN_RETRY_DELAYS_MS[attempt];
        const recoverable = isRecoverableJoinError(error);
        if (recoverable) {
          await this.leaveRoomBestEffort(engine);
        }
        if (retryDelay === undefined || !recoverable) {
          throw error;
        }
        this.logger?.log('rtc', 'rtc_join_retry_scheduled', {
          attempt: attempt + 2,
          delay_ms: retryDelay,
        });
        await this.waitForJoinRetry(retryDelay);
      }
    }
    return false;
  }

  private waitForJoinRetry(delayMs: number): Promise<void> {
    return new Promise((resolve) => {
      const finish = () => {
        this.joinRetryTimer = null;
        this.joinRetryResolve = null;
        resolve();
      };
      this.joinRetryResolve = finish;
      this.joinRetryTimer = setTimeout(finish, delayMs);
    });
  }

  private cancelJoinRetry(): void {
    if (this.joinRetryTimer !== null) {
      clearTimeout(this.joinRetryTimer);
      this.joinRetryTimer = null;
    }
    const resolve = this.joinRetryResolve;
    this.joinRetryResolve = null;
    resolve?.();
  }

  private async leaveRoomBestEffort(engine: VolcRtcEngineLike): Promise<void> {
    try {
      await engine.leaveRoom();
    } catch {
      // 失败进房和断线恢复前的清理是 best effort，不能覆盖真正的进房错误。
    }
  }

  private async unsubscribeAllRemotes(engine: VolcRtcEngineLike): Promise<void> {
    const remotes = [...this.remoteMediaTypes.entries()];
    await Promise.all(
      remotes.map(async ([userId, mediaType]) => {
        const subscribeType = this.subscribeAudio ? mediaType : mediaType & MEDIA_VIDEO;
        if (subscribeType === 0) {
          return;
        }
        try {
          await engine.unsubscribeStream(userId, subscribeType);
        } catch {
          // 退房前退订失败不能挡住后续停采集。
        }
      }),
    );
  }

  private clearRemoteState(): void {
    this.remoteMediaTypes.clear();
    this.remoteBindings = [];
    if (this.useSdkRemotePlayer) {
      if (this.remoteSdkPlayerBoundUid) {
        this.unbindSdkRemotePlayer(this.remoteSdkPlayerBoundUid);
        this.remoteSdkPlayerBoundUid = null;
        this.remoteSdkVideoElement = null;
      }
    } else if (this.remoteVideoElement) {
      this.remoteVideoElement.srcObject = null;
    }
    this.onRemoteBindingsChange([]);
  }

  private bindEngineEvents(engine: VolcRtcEngineLike, sdk: VolcRtcSdkModule): void {
    const events = sdk.default.events;

    const onConnectionStateChanged = (event: unknown) => {
      if (this.disposed || this.engine !== engine) {
        return;
      }
      const state = (event as { state?: number }).state;
      if (state !== this.lastConnectionState) {
        this.logger?.log('rtc', 'rtc_connection_state_changed', { state, previous_state: this.lastConnectionState });
        this.lastConnectionState = state;
      }
      if (state === CONNECTION_CONNECTED) {
        this.onConnectionEstablished();
        return;
      }
      if (state === CONNECTION_RECONNECTED) {
        void this.restoreLocalMedia()
          .then(() => {
            if (this.disposed || this.engine !== engine) {
              return;
            }
            this.applyLocalView();
            this.applyRemoteView();
            this.onConnectionEstablished();
          })
          .catch((error) => {
            if (this.disposed || this.engine !== engine) {
              return;
            }
            this.logger?.log('rtc', 'rtc_reconnect_media_restore_failed', {
              error_message: sanitizeString(normalizeError(error)),
            });
            this.onConnectionFailure();
          });
        return;
      }
      if (state === CONNECTION_RECONNECTING) {
        this.onConnectionRecovering();
        return;
      }
      if (state === CONNECTION_DISCONNECTED || state === CONNECTION_LOST) {
        this.onConnectionFailure();
      }
    };

    const onTrackEnded = (event: unknown) => {
      if (this.disposed || this.engine !== engine) {
        return;
      }
      const payload = event as { isScreen?: boolean; kind?: string };
      this.logger?.log('rtc', 'rtc_track_ended', { kind: payload.kind, is_screen: payload.isScreen });
      if (payload.kind === 'audio' && !payload.isScreen && this.desiredLocalAudioEnabled) {
        this.onLocalAudioRestoreError('audio_track_ended');
        this.queueLocalAudioRecovery('track_ended');
      }
    };

    const onAudioDeviceStateChanged = (event: unknown) => {
      if (this.disposed || this.engine !== engine) {
        return;
      }
      const payload = event as {
        deviceState?: 'active' | 'inactive';
        mediaDeviceInfo?: { deviceId?: string; kind?: string };
      };
      const deviceId = payload.mediaDeviceInfo?.deviceId?.trim();
      if (payload.mediaDeviceInfo?.kind !== 'audioinput' || !deviceId) {
        return;
      }
      if (payload.deviceState === 'inactive') {
        if (this.audioCaptureDeviceId && deviceId !== this.audioCaptureDeviceId) {
          return;
        }
        this.queueLocalAudioSuspension('audio_device_disconnected');
        return;
      }
      if (payload.deviceState === 'active' && this.desiredLocalAudioEnabled) {
        if (this.isRecoveringLocalAudio) {
          this.pendingAudioRecoveryDeviceId = deviceId;
          return;
        }
        if (!this.hasStartedAudioCapture) {
          this.queueLocalAudioRecovery('audio_device_connected', deviceId);
        }
      }
    };

    const onUserPublishStream = (event: unknown) => {
      if (this.disposed || this.engine !== engine) {
        return;
      }
      const payload = event as { mediaType?: number; userId?: string };
      const userId = payload.userId;
      const mediaType = payload.mediaType ?? 0;
      if (!userId || userId === this.localUserId) {
        return;
      }
      this.logger?.log('rtc', 'rtc_remote_published', { user_id: userId, media_type: mediaType });
      void this.handleRemotePublish(userId, mediaType).catch((error) => {
        if (this.disposed || this.engine !== engine) {
          return;
        }
        const remainingMediaType = (this.remoteMediaTypes.get(userId) ?? 0) & ~mediaType;
        if (remainingMediaType === 0) {
          this.remoteMediaTypes.delete(userId);
        } else {
          this.remoteMediaTypes.set(userId, remainingMediaType);
        }
        this.logger?.log('rtc', 'rtc_subscribe_failed', {
          error_message: sanitizeString(normalizeError(error)),
          media_type: mediaType,
          user_id: userId,
        });
      });
    };

    const onUserUnpublishStream = (event: unknown) => {
      if (this.disposed || this.engine !== engine) {
        return;
      }
      const payload = event as { mediaType?: number; userId?: string };
      const userId = payload.userId;
      if (!userId) {
        return;
      }
      const nextType = (this.remoteMediaTypes.get(userId) ?? 0) & ~(payload.mediaType ?? 0);
      if (nextType === 0) {
        this.remoteMediaTypes.delete(userId);
        this.remoteBindings = this.remoteBindings.filter((binding) => binding.uid !== userId);
      } else {
        this.remoteMediaTypes.set(userId, nextType);
        this.upsertRemoteBinding(
          userId,
          this.subscribeAudio && hasMediaType(nextType, MEDIA_AUDIO),
          hasMediaType(nextType, MEDIA_VIDEO),
        );
      }
      this.emitRemotePresence(userId);
      this.onRemoteBindingsChange([...this.remoteBindings]);
      this.applyRemoteView();
    };

    const onAutoplayFailed = (event: unknown) => {
      if (this.disposed || this.engine !== engine) {
        return;
      }
      const kind = (event as { kind?: AutoPlayBlockedKind }).kind;
      if (kind !== 'audio' && kind !== 'video') {
        return;
      }
      // 火山 SDK 内部播放器失败噪声大。画面已在播只说明 video 通了；
      // 音频走 SDK，失败交给上层「没测到静音包就不弹」。
      if (this.isRemotePlaybackActive(kind)) {
        return;
      }
      this.reportAutoPlayBlocked(kind);
    };

    const onRemoteStreamStats = (value: unknown) => {
      if (this.disposed || this.engine !== engine) {
        return;
      }
      const stats = value as VolcRemoteStats;
      const audio = stats.audioStats;
      const video = stats.videoStats;
      const payload = {
        audio_e2e_delay_ms: audio?.e2eDelay,
        audio_loss_rate: audio?.audioLossRate,
        audio_received_kbps: audio?.receivedKBitrate,
        decode_fps: video?.decoderOutputFrameRate,
        height: video?.height,
        is_screen: stats.isScreen,
        render_fps: video?.rendererOutputFrameRate,
        rtt_ms: video?.rtt ?? audio?.rtt,
        user_id: stats.userId,
        video_e2e_delay_ms: video?.e2eDelay,
        video_loss_rate: video?.videoLossRate,
        video_received_kbps: video?.receivedKBitrate,
        width: video?.width,
      };
      this.handleRemoteStats(stats);
      this.logDownlinkStreamStats(payload, {
        videoFps: video?.rendererOutputFrameRate,
        audioKbps: audio?.receivedKBitrate ?? 0,
        height: video?.height,
        videoKbps: video?.receivedKBitrate ?? 0,
        width: video?.width,
      });
    };

    const onLocalAudioPropertiesReport = (value: unknown) => {
      if (this.disposed || this.engine !== engine) {
        return;
      }
      const reports = Array.isArray(value) ? value : [];
      for (const report of reports) {
        const item = report as {
          audioPropertiesInfo?: { linearVolume?: number; nonlinearVolume?: number };
          streamIndex?: number;
        };
        if (item.streamIndex === undefined || item.streamIndex === STREAM_INDEX_MAIN) {
          this.lastLocalAudioLinearVolume = item.audioPropertiesInfo?.linearVolume ?? 0;
        }
      }
    };

    const onNetworkQuality = (uplink: unknown, downlink: unknown) => {
      if (this.disposed || this.engine !== engine || typeof uplink !== 'number' || typeof downlink !== 'number') {
        return;
      }
      this.onNetworkQualityChange({ downlink, uplink });
      const qualityKey = `${uplink}:${downlink}`;
      if (qualityKey !== this.lastNetworkQualityKey) {
        this.logger?.log('rtc', 'rtc_network_quality_changed', { uplink, downlink });
        this.lastNetworkQualityKey = qualityKey;
      }
      void this.updateEncoderForUplinkQuality(uplink);
    };

    const onLocalStreamStats = (value: unknown) => {
      if (this.disposed || this.engine !== engine) {
        return;
      }
      const stats = value as LocalStreamStats;
      const audio = stats.audioStats;
      const video = stats.videoStats;
      if (!stats.isScreen) {
        this.onLocalVideoStatsChange?.({
          sampledAtMs: Date.now(),
          sentFps: video?.sentFrameRate,
          sentKbps: video?.sentKBitrate,
          inputFps: video?.inputFrameRate,
        });
        if (!this.hasObservedVideoSent && (video?.sentFrameRate ?? 0) > 0 && (video?.sentKBitrate ?? 0) > 0) {
          this.hasObservedVideoSent = true;
          this.logger?.markOnce('rtc_first_video_sent');
          this.logger?.log('rtc', 'rtc_first_video_sent', {
            evidence: 'sdk_local_stream_stats',
            sent_fps: video?.sentFrameRate,
            sent_kbps: video?.sentKBitrate,
            elapsed_ms_since_join: this.logger?.since('rtc_join_channel_done'),
            elapsed_ms_since_claim: this.logger?.since('claim_success'),
          });
        }
      }
      if ((audio?.sendKBitrate ?? 0) > 0 || (video?.encodedFrameCount ?? 0) > 0) {
        this.logger?.markOnce('local_first_frame_sent');
      }
      if (
        this.hasPublishedAudio &&
        this.desiredLocalAudioEnabled &&
        this.lastLocalAudioLinearVolume >= LOCAL_AUDIO_SPEAKING_VOLUME_THRESHOLD &&
        audio?.sendKBitrate !== undefined &&
        audio.sendKBitrate <= 0
      ) {
        this.localAudioStallSamples += 1;
        if (this.localAudioStallSamples >= LOCAL_AUDIO_STALL_SAMPLE_THRESHOLD) {
          this.logger?.log('rtc', 'rtc_local_audio_uplink_stalled', {
            linear_volume: this.lastLocalAudioLinearVolume,
            sample_count: this.localAudioStallSamples,
            send_kbps: audio.sendKBitrate,
          });
          this.queueLocalAudioRecovery('uplink_stalled');
        }
      } else {
        this.localAudioStallSamples = 0;
      }
      const payload = {
        audio_loss_rate: audio?.audioLossRate,
        audio_record_sample_rate: audio?.recordSampleRate,
        audio_sent_kbps: audio?.sendKBitrate,
        encoded_frame_count: video?.encodedFrameCount,
        encoded_height: video?.encodedFrameHeight,
        encoded_width: video?.encodedFrameWidth,
        encoder_output_fps: video?.encoderOutputFrameRate,
        input_fps: video?.inputFrameRate,
        is_screen: stats.isScreen,
        loss_rate: video?.videoLossRate,
        mic_enabled: this.desiredLocalAudioEnabled,
        rtt_ms: video?.rtt,
        sent_fps: video?.sentFrameRate,
        sent_kbps: video?.sentKBitrate,
      };
      this.logUplinkStreamStats(payload, {
        videoFps: video?.sentFrameRate,
        audioKbps: audio?.sendKBitrate ?? 0,
        height: video?.encodedFrameHeight,
        videoKbps: video?.sentKBitrate ?? 0,
        width: video?.encodedFrameWidth,
      });
    };

    const logSdkOperationResult = (operation: 'publish' | 'subscribe', event: unknown) => {
      this.logger?.log('rtc', `rtc_${operation}_result`, {
        result: sanitizeString(normalizeError(event)),
      });
    };

    const onPublishResult = (event: unknown) => {
      if (!this.disposed && this.engine === engine) {
        logSdkOperationResult('publish', event);
      }
    };

    const onSubscribeResult = (event: unknown) => {
      if (!this.disposed && this.engine === engine) {
        logSdkOperationResult('subscribe', event);
      }
    };

    const onLocalStatsException = (event: unknown) => {
      if (!this.disposed && this.engine === engine) {
        this.logger?.log('rtc', 'rtc_local_stats_exception', {
          detail: sanitizeString(normalizeError(event)),
        });
      }
    };

    const onLocalVideoSizeChanged = (value: unknown) => {
      if (this.disposed || this.engine !== engine) {
        return;
      }
      const event = value as {
        info?: { height?: number; width?: number };
        streamIndex?: number;
      };
      const payload = {
        height: event.info?.height,
        stream_index: event.streamIndex,
        width: event.info?.width,
      };
      const sizeKey = `${payload.width ?? ''}x${payload.height ?? ''}:${payload.stream_index ?? ''}`;
      if (sizeKey === this.lastLocalVideoSizeKey) {
        return;
      }
      this.lastLocalVideoSizeKey = sizeKey;
      this.logger?.log('rtc', 'rtc_local_video_size_changed', payload);
      if (process.env.NODE_ENV === 'development') {
        console.info('[volc-rtc] local-video-size', payload);
      }
    };

    const onRemoteVideoFirstFrame = () => {
      if (this.disposed || this.engine !== engine) {
        return;
      }
      this.applyRemoteView();
      if (!this.hasObservedRemoteFrame) {
        this.hasObservedRemoteFrame = true;
        this.logger?.markOnce('rtc_remote_first_frame');
        this.logger?.log('rtc', 'rtc_remote_first_frame', {
          elapsed_ms_since_join: this.logger?.since('rtc_join_channel_done'),
          elapsed_ms_since_claim: this.logger?.since('claim_success'),
        });
      }
      this.onRemoteVideoFirstFrame();
    };

    const onError = (event: unknown) => {
      if (this.disposed || this.engine !== engine) {
        return;
      }
      this.logger?.log('rtc', 'rtc_error', { error_code: sanitizeString(normalizeRtcErrorCode(event)) });
      if (normalizeRtcErrorCode(event).includes('RECONNECT_FAILED')) {
        this.onConnectionFailure();
        return;
      }
      if (isTerminalJoinError(event)) {
        this.onFatalError(normalizeRtcErrorCode(event));
        return;
      }
      this.onConnectionFailure();
    };

    engine.on(events.onConnectionStateChanged, onConnectionStateChanged);
    engine.on(events.onTrackEnded, onTrackEnded);
    engine.on(events.onAudioDeviceStateChanged, onAudioDeviceStateChanged);
    engine.on(events.onUserPublishStream, onUserPublishStream);
    engine.on(events.onUserUnpublishStream, onUserUnpublishStream);
    engine.on(events.onAutoplayFailed, onAutoplayFailed);
    engine.on(events.onRemoteStreamStats, onRemoteStreamStats);
    engine.on(events.onLocalAudioPropertiesReport, onLocalAudioPropertiesReport);
    engine.on(events.onNetworkQuality, onNetworkQuality);
    engine.on(events.onLocalStreamStats, onLocalStreamStats);
    engine.on(events.onLocalVideoSizeChanged, onLocalVideoSizeChanged);
    engine.on(events.onRemoteVideoFirstFrame, onRemoteVideoFirstFrame);
    engine.on(events.onPublishResult, onPublishResult);
    engine.on(events.onSubscribeResult, onSubscribeResult);
    engine.on(events.onLocalStatsException, onLocalStatsException);
    engine.on(events.onError, onError);

    this.eventDisposers = [
      () => engine.off(events.onConnectionStateChanged, onConnectionStateChanged),
      () => engine.off(events.onTrackEnded, onTrackEnded),
      () => engine.off(events.onAudioDeviceStateChanged, onAudioDeviceStateChanged),
      () => engine.off(events.onUserPublishStream, onUserPublishStream),
      () => engine.off(events.onUserUnpublishStream, onUserUnpublishStream),
      () => engine.off(events.onAutoplayFailed, onAutoplayFailed),
      () => engine.off(events.onRemoteStreamStats, onRemoteStreamStats),
      () => engine.off(events.onLocalAudioPropertiesReport, onLocalAudioPropertiesReport),
      () => engine.off(events.onNetworkQuality, onNetworkQuality),
      () => engine.off(events.onLocalStreamStats, onLocalStreamStats),
      () => engine.off(events.onLocalVideoSizeChanged, onLocalVideoSizeChanged),
      () => engine.off(events.onRemoteVideoFirstFrame, onRemoteVideoFirstFrame),
      () => engine.off(events.onPublishResult, onPublishResult),
      () => engine.off(events.onSubscribeResult, onSubscribeResult),
      () => engine.off(events.onLocalStatsException, onLocalStatsException),
      () => engine.off(events.onError, onError),
    ];
  }

  private unbindEngineEvents(): void {
    for (const dispose of this.eventDisposers) {
      dispose();
    }
    this.eventDisposers = [];
  }

  private async handleRemotePublish(userId: string, mediaType: number): Promise<void> {
    const engine = this.engine;
    if (!engine) {
      return;
    }

    const previous = this.remoteMediaTypes.get(userId) ?? 0;
    const nextType = previous | mediaType;
    this.remoteMediaTypes.set(userId, nextType);

    const subscribeType = this.subscribeAudio ? mediaType : mediaType & MEDIA_VIDEO;
    if (subscribeType !== 0) {
      await engine.subscribeStream(userId, subscribeType);
      if (this.disposed || this.engine !== engine) {
        try {
          await engine.unsubscribeStream(userId, subscribeType);
        } catch {
          // 关闭过程中订阅才完成时补一次退订，避免下行在结束页继续收流。
        }
        return;
      }
    } else if (this.disposed || this.engine !== engine) {
      return;
    }
    const hasVideo = hasMediaType(nextType, MEDIA_VIDEO);
    const hasAudio = this.subscribeAudio && hasMediaType(nextType, MEDIA_AUDIO);

    this.upsertRemoteBinding(userId, hasAudio, hasVideo);
    this.emitRemotePresence(userId);
    this.onRemoteBindingsChange([...this.remoteBindings]);
    this.applyRemoteView();
  }

  private upsertRemoteBinding(userId: string, hasAudio: boolean, hasVideo: boolean): void {
    const nextBinding: VolcRemoteBinding = {
      hasAudio,
      uid: userId,
      videoTrack: hasVideo ? VOLC_REMOTE_VIDEO_TRACK_CAMERA : 0,
    };
    const index = this.remoteBindings.findIndex((binding) => binding.uid === userId);
    if (index >= 0) {
      this.remoteBindings[index] = nextBinding;
      return;
    }
    this.remoteBindings.push(nextBinding);
  }

  private emitRemotePresence(userId: string): void {
    const mediaType = this.remoteMediaTypes.get(userId) ?? 0;
    this.onRemoteTrackPresenceChange({
      hasAudioTrack: this.subscribeAudio && hasMediaType(mediaType, MEDIA_AUDIO),
      hasVideoTrack: hasMediaType(mediaType, MEDIA_VIDEO),
      uid: userId,
    });
  }

  private logUplinkStreamStats(payload: Record<string, LiveDebugValue>, fingerprint: DevStreamStatsFingerprint): void {
    const now = Date.now();
    if (!shouldLogDevStreamStats(this.lastUplinkStatsLog, fingerprint, now)) {
      return;
    }
    this.lastUplinkStatsLog.fingerprint = fingerprint;
    this.lastUplinkStatsLog.loggedAt = now;
    this.logger?.log('rtc', 'rtc_uplink_stats', payload);
    if (process.env.NODE_ENV === 'development') {
      console.info('[volc-rtc] uplink', payload);
    }
  }

  private logDownlinkStreamStats(
    payload: Record<string, LiveDebugValue>,
    fingerprint: DevStreamStatsFingerprint,
  ): void {
    const now = Date.now();
    const state = this.lastDownlinkStatsLog;
    if (!shouldLogDevStreamStats(state, fingerprint, now)) {
      return;
    }
    state.fingerprint = fingerprint;
    state.loggedAt = now;
    this.logger?.log('rtc', 'rtc_downlink_stats', payload);
    if (process.env.NODE_ENV === 'development') {
      console.info('[volc-rtc] downlink', payload);
    }
  }

  private handleRemoteStats(stats: VolcRemoteStats): void {
    if (stats.isScreen || !stats.userId) {
      return;
    }

    const intervalMs = stats.videoStats?.statsInterval ?? stats.audioStats?.statsInterval ?? 2000;
    const intervalSeconds = intervalMs / 1000;
    const videoKbps = stats.videoStats?.receivedKBitrate ?? 0;
    const audioKbps = stats.audioStats?.receivedKBitrate ?? 0;
    this.accumulatedVideoBytes += (videoKbps * 1000 * intervalSeconds) / 8;
    this.accumulatedAudioBytes += (audioKbps * 1000 * intervalSeconds) / 8;
    // 火山远端统计没有 packetsReceived。/live 用包增量判断 audio flowing，
    // 这里用码率换算累计包数，避免 SDK 误报拦截后提示一直不收。
    if (audioKbps > 0) {
      this.accumulatedAudioPackets += Math.max(1, Math.round(audioKbps * intervalSeconds));
    }
    if (videoKbps > 0) {
      this.accumulatedVideoPackets += Math.max(1, Math.round(videoKbps * intervalSeconds));
    }
    const decodeFps = stats.videoStats?.decoderOutputFrameRate ?? 0;
    const renderFps = stats.videoStats?.rendererOutputFrameRate ?? decodeFps;
    this.accumulatedVideoFramesDecoded += decodeFps * intervalSeconds;
    this.accumulatedVideoFramesReceived += decodeFps * intervalSeconds;
    this.accumulatedVideoFramesRendered += renderFps * intervalSeconds;

    const mediaType = this.remoteMediaTypes.get(stats.userId) ?? 0;
    this.onRemoteMediaStatsChange({
      audioE2eDelayMs: stats.audioStats?.e2eDelay,
      audioBytesReceived: this.accumulatedAudioBytes,
      audioPacketsReceived: this.accumulatedAudioPackets,
      hasAudioTrack: this.subscribeAudio && hasMediaType(mediaType, MEDIA_AUDIO),
      hasVideoTrack: hasMediaType(mediaType, MEDIA_VIDEO),
      sampledAtMs: Date.now(),
      subscribeLoss: stats.videoStats?.videoLossRate ?? stats.audioStats?.audioLossRate,
      subscribeRttMs: stats.videoStats?.rtt ?? stats.audioStats?.rtt,
      uid: stats.userId,
      videoBytesReceived: this.accumulatedVideoBytes,
      videoDecodeFps: decodeFps,
      videoFrameHeight: stats.videoStats?.height,
      videoFrameWidth: stats.videoStats?.width,
      videoFramesDecoded: this.accumulatedVideoFramesDecoded,
      videoFramesReceived: this.accumulatedVideoFramesReceived,
      videoFramesRendered: this.accumulatedVideoFramesRendered,
      videoPacketsReceived: this.accumulatedVideoPackets,
      videoE2eDelayMs: stats.videoStats?.e2eDelay,
    });
  }

  private resolveEncoderConfig(): VideoEncoderConfig {
    const config =
      this.currentEncoderProfile === 'degraded' ? VOLC_DEGRADED_VIDEO_ENCODER_CONFIG : VOLC_VIDEO_ENCODER_CONFIG;
    return this.targetVideoFrameRate
      ? {
          ...config,
          frameRate: {
            min: this.targetVideoFrameRate,
            ideal: this.targetVideoFrameRate,
            max: this.targetVideoFrameRate,
          },
        }
      : config;
  }

  /**
   * 单流动态降级：弱网时分辨率和码率一起降，恢复后再升回高清档。
   * 只有质量明确好或明确差时才切档，中间地带保持现状，避免在边界反复重配编码器。
   */
  private async updateEncoderForUplinkQuality(uplink: number): Promise<void> {
    const engine = this.engine;
    if (
      !engine ||
      !this.hasJoined ||
      this.capsUplinkAtDegraded ||
      (!this.localVideoStream && !this.isScreenShareActive)
    ) {
      return;
    }
    const nextProfile =
      uplink >= POOR_UPLINK_QUALITY ? 'degraded' : uplink <= GOOD_UPLINK_QUALITY ? 'high' : this.currentEncoderProfile;
    if (nextProfile === this.currentEncoderProfile) {
      return;
    }

    const previousProfile = this.currentEncoderProfile;
    this.currentEncoderProfile = nextProfile;
    const config = this.resolveEncoderConfig();
    try {
      await engine.setVideoEncoderConfig({
        ...config,
        contentHint: this.currentContentHint,
      });
      this.logger?.log('rtc', 'rtc_encoder_profile_adapted', {
        max_kbps: config.maxKbps,
        profile: nextProfile,
        uplink_quality: uplink,
      });
    } catch (error) {
      this.currentEncoderProfile = previousProfile;
      this.logger?.log('rtc', 'rtc_encoder_profile_adapt_failed', {
        error_message: sanitizeString(normalizeError(error)),
        uplink_quality: uplink,
      });
    }
  }

  private releaseRetainedEngine(): void {
    this.releaseEngineRetention?.();
    this.releaseEngineRetention = null;
  }
}
